import { useState, useEffect } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  wasOffline: boolean;
}

/**
 * Custom hook to monitor network connectivity status
 *
 * @returns {NetworkStatus} Current network status
 * - isOnline: true if connected, false if disconnected
 * - wasOffline: true if was previously offline (for showing reconnection message)
 */
export const useNetworkStatus = (): NetworkStatus => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [wasOffline, setWasOffline] = useState<boolean>(false);

  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Network: Connection restored');
      setIsOnline(true);
      setWasOffline(true);

      // Clear "was offline" flag after 5 seconds
      setTimeout(() => {
        setWasOffline(false);
      }, 5000);
    };

    const handleOffline = () => {
      console.log('🔌 Network: Connection lost');
      setIsOnline(false);
      setWasOffline(false);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline, wasOffline };
};

export default useNetworkStatus;
